import { useCallback } from 'react'

import {
    NATIVE_MINT,
    TOKEN_PROGRAM_ID,
    createCloseAccountInstruction,
    createInitializeAccountInstruction,
    createSyncNativeInstruction,
    createTransferInstruction,
    getAssociatedTokenAddress,
    getMinimumBalanceForRentExemptAccount,
} from '@solana/spl-token'
import { useWallet } from '@solana/wallet-adapter-react'
import { Keypair, PublicKey, SystemProgram, Transaction } from '@solana/web3.js'

import { getHighPriorityConnection } from '@src/chains/solana/anchor/setup'
import { confirmTransactionWithRetry, sendTransactionWithRetry } from '@src/chains/solana/utils/retryConnection'
import { ChainId, WRAPPED_GAS_TOKENS } from '@src/configs/chainInfo'
import { createContextLogger } from '@src/utils'

const logger = createContextLogger('useUnWrapWSOL')

/**
 * Hook for unwrapping WSOL -> SOL (including partial unwrap).
 *
 * Steps to unwrap <amount> lamports:
 *  1) Create an ephemeral (temporary) native token account with enough rent + <amount> lamports.
 *  2) Transfer <amount> from your main wSOL ATA -> ephemeral account as SPL tokens.
 *  3) Sync ephemeral account so it becomes recognized as native SOL.
 *  4) Close ephemeral account -> returns lamports to your wallet.
 */
export const useUnWrapWSOL = () => {
    const { publicKey, signTransaction } = useWallet()

    /**
     * Partially or fully unwrap <amountLamports> from wSOL into native SOL.
     * @param amountLamports BigInt of lamports to unwrap
     * @returns Transaction signature
     */
    const unwrapSol = useCallback(
        async (amountLamports?: bigint): Promise<string> => {
            if (!publicKey) throw new Error('Wallet not connected')
            if (!signTransaction) throw new Error('Wallet does not support signing')
            if (!amountLamports || amountLamports <= BigInt(0)) {
                throw new Error('Must unwrap a positive amount of WSOL (lamports)')
            }

            try {
                logger.info('Unwrapping partial WSOL -> SOL', {
                    user: publicKey.toString(),
                    amountLamports: amountLamports.toString(),
                })

                // 1) Use a high-priority RPC connection
                const connection = getHighPriorityConnection()

                // 2) The WSOL mint (native mint) from config
                const wsolMint = new PublicKey(WRAPPED_GAS_TOKENS[ChainId.Solana].address)
                // Your existing WSOL ATA
                const sourceWsolATA = await getAssociatedTokenAddress(wsolMint, publicKey)

                // 3) Create ephemeral Keypair for a "temporary" native token account
                const ephemeralAccount = Keypair.generate()

                // 3a) Calculate how much rent this ephemeral account needs to be rent-exempt
                const rentExempt = await getMinimumBalanceForRentExemptAccount(connection)

                // 4) Build instructions:

                // -> i) Create the ephemeral account (SystemProgram.createAccount)
                //    that will hold the partial WSOL to be unwrapped.
                const createAccountIx = SystemProgram.createAccount({
                    fromPubkey: publicKey,
                    newAccountPubkey: ephemeralAccount.publicKey,
                    lamports: rentExempt, // Fund with enough for rent
                    space: 165, // Token account size
                    programId: TOKEN_PROGRAM_ID,
                })

                // -> ii) Initialize ephemeral account as a native token account (for the NATIVE_MINT).
                //        We'll set 'publicKey' as the owner.
                const initAccountIx = createInitializeAccountInstruction(
                    ephemeralAccount.publicKey, // new account
                    NATIVE_MINT, // always NATIVE_MINT for wSOL
                    publicKey, // owner
                    TOKEN_PROGRAM_ID
                )

                // -> iii) Transfer <amountLamports> from your main wSOL ATA -> ephemeral account
                //         as an SPL Token "transfer" (in tokens of decimal=9).
                //         1 token = 1 lamport if we consider wSOL's decimals=9, so the "amount" is <amountLamports>.
                const transferIx = createTransferInstruction(
                    sourceWsolATA, // from wSOL ATA
                    ephemeralAccount.publicKey, // to ephemeral account
                    publicKey, // authority
                    Number(amountLamports), // partial wSOL in base units
                    [], // multiSigners
                    TOKEN_PROGRAM_ID
                )

                // -> iv) Sync ephemeral account so those tokens become recognized as native lamports.
                const syncIx = createSyncNativeInstruction(ephemeralAccount.publicKey)

                // -> v) Close ephemeral account, returning lamports to your wallet as SOL
                const closeIx = createCloseAccountInstruction(
                    ephemeralAccount.publicKey,
                    publicKey, // SOL goes back to your main wallet
                    publicKey, // authority
                    [], // multiSigners
                    TOKEN_PROGRAM_ID
                )

                /**
                 * IMPORTANT: Order of signatures matter! We first have to sign with the wallet, and use
                 * the ephemeral accounts signature only when wallet signature is already added.
                 */

                // 5) Assemble transaction
                const { blockhash } = await connection.getLatestBlockhash()
                const tx = new Transaction()
                tx.add(createAccountIx, initAccountIx, transferIx, syncIx, closeIx)
                tx.recentBlockhash = blockhash
                tx.feePayer = publicKey

                // 6) First, let the wallet sign the transaction.
                const walletSignedTx = await signTransaction(tx)

                // 7) Then, add the ephemeral account’s signature.
                walletSignedTx.partialSign(ephemeralAccount)

                // (Optional) Simulate the fully signed transaction.
                const simulationResult = await connection.simulateTransaction(walletSignedTx)
                logger.info('Unwrap Sol Simulation result:', simulationResult)

                // 8) Send the raw transaction with retry mechanism.
                const signature = await sendTransactionWithRetry(walletSignedTx, {
                    skipPreflight: true,
                    preflightCommitment: 'processed',
                    maxRetries: 5,
                })

                logger.info('Unwrap Sol signatures:', walletSignedTx.signatures)
                logger.info('Partial unwrap transaction sent', { signature })
                return signature
            } catch (error) {
                logger.error('Error unwrapping partial WSOL:', { error })
                throw error
            }
        },
        [publicKey, signTransaction]
    )

    /**
     * Wait for transaction confirmation.
     * @param txSignature the signature from unwrapSol
     */
    const waitForUnwrap = useCallback(async (txSignature: string) => {
        try {
            await confirmTransactionWithRetry(txSignature, {
                preflightCommitment: 'processed',
                maxRetries: 5,
            })
            logger.info('Unwrap transaction confirmed', { txSignature })
        } catch (error) {
            logger.error('Error waiting for unwrapSol confirmation:', { error })
            throw error
        }
    }, [])

    return { unwrapSol, waitForUnwrap }
}
